import React, { useState, useCallback } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { setOracleToken } from "../../services/apiV2";
import MarketTicker from "../MarketTicker";
import styles from "./SentinelLayout.module.css";

export default function SentinelLayout() {
    const location = useLocation();
    const isActive = (path) => location.pathname === path;

    // ── Layout-Level Auth Gate ──
    const [isLocked, setIsLocked] = useState(true);
    const [unlockToken, setUnlockToken] = useState("");
    const [shakeError, setShakeError] = useState(false);

    const handleUnlock = useCallback((e) => {
        e.preventDefault();
        const token = unlockToken.trim();
        if (token.length > 0) {
            setOracleToken(token);
            setIsLocked(false);
        } else {
            setShakeError(true);
            setTimeout(() => setShakeError(false), 500);
        }
    }, [unlockToken]);

    const handleLock = useCallback(() => {
        setOracleToken(null);
        setIsLocked(true);
        setUnlockToken("");
    }, []);

    const links = [
        { path: "/v2/dashboard", label: "Dashboard", icon: "📊" },
        { path: "/v2/explorer", label: "Chain Explorer", icon: "🔗" },
        { path: "/v2/audit", label: "Audit Lab", icon: "🧪" },
    ];

    // ── Locked State: Full-Screen Auth Portal ──
    if (isLocked) {
        return (
            <div className={styles.sentinelContainer}>
                <header className={styles.topbar}>
                    <MarketTicker />
                </header>
                <div style={{
                    minHeight: "80vh", display: "flex", justifyContent: "center",
                    alignItems: "center", fontFamily: "'Inter', sans-serif"
                }}>
                    <div style={{
                        padding: 40,
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        borderRadius: 16,
                        textAlign: "center",
                        background: "rgba(15, 15, 15, 0.8)",
                        backdropFilter: "blur(20px)",
                        boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
                        maxWidth: 400,
                        width: "100%",
                        animation: shakeError ? "sentinelShake 0.4s ease-in-out" : "none"
                    }}>
                        <style>{`
                            @keyframes sentinelShake {
                                0% { transform: translateX(0); }
                                25% { transform: translateX(-8px); }
                                50% { transform: translateX(8px); }
                                75% { transform: translateX(-8px); }
                                100% { transform: translateX(0); }
                            }
                        `}</style>
                        <div style={{ fontSize: 50, marginBottom: 20 }}>🔐</div>
                        <h2 style={{ color: "#00FF94", marginBottom: 10, letterSpacing: 1 }}>
                            SENTINEL ACCESS
                        </h2>
                        <p style={{ opacity: 0.6, marginBottom: 30, fontSize: "0.9rem", color: "#aaa" }}>
                            Encryption active. Oracle token required.
                        </p>
                        <form onSubmit={handleUnlock}>
                            <input
                                type="password"
                                value={unlockToken}
                                onChange={e => setUnlockToken(e.target.value)}
                                placeholder="Oracle Token..."
                                style={{
                                    display: "block", width: "100%", padding: "14px 18px", marginBottom: 20,
                                    background: "rgba(0, 0, 0, 0.3)", border: "1px solid rgba(0, 255, 148, 0.2)",
                                    color: "white", borderRadius: 8, fontSize: "1rem", outline: "none",
                                    boxSizing: "border-box"
                                }}
                                autoFocus
                            />
                            <button
                                type="submit"
                                style={{
                                    width: "100%", padding: "14px", background: "#00FF94", color: "black",
                                    border: "none", borderRadius: 8, fontWeight: "bold", cursor: "pointer",
                                    fontSize: "1rem", transition: "all 0.2s",
                                    boxShadow: "0 0 15px rgba(0, 255, 148, 0.3)"
                                }}
                                onMouseOver={e => e.target.style.transform = "translateY(-2px)"}
                                onMouseOut={e => e.target.style.transform = "translateY(0)"}
                            >
                                UNLOCK VAULT
                            </button>
                        </form>
                        <div style={{ marginTop: 20 }}>
                            <Link to="/" style={{ color: "#666", textDecoration: "none", fontSize: "0.8rem" }}>
                                ⬅️ Retour à l'accueil
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── Unlocked State: Full Sentinel Layout ──
    return (
        <div className={styles.sentinelContainer}>
            <header className={styles.topbar}>
                <MarketTicker />
            </header>

            <div className={styles.mainWrapper}>
                <aside className={styles.sidebar}>
                    {links.map((link) => (
                        <Link
                            key={link.path}
                            to={link.path}
                            className={`${styles.sidebarLink} ${isActive(link.path) ? styles.activeLink : ""}`}
                        >
                            <span className={styles.icon}>{link.icon}</span>
                            <span className={styles.label}>{link.label}</span>
                        </Link>
                    ))}

                    {/* Lock button in sidebar */}
                    <button
                        onClick={handleLock}
                        className={styles.sidebarLink}
                        style={{
                            background: "none", border: "none", cursor: "pointer",
                            color: "#FF5555", textAlign: "left", width: "100%",
                            padding: "10px 16px", fontSize: "inherit", fontFamily: "inherit"
                        }}
                        title="Verrouiller Sentinel"
                    >
                        <span className={styles.icon}>🔒</span>
                        <span className={styles.label}>Lock</span>
                    </button>

                    <div className={styles.backLink}>
                        <Link to="/">
                            <span className={styles.icon}>⬅️</span>
                            <span className={styles.label}>Back to V1</span>
                        </Link>
                    </div>
                </aside>

                <main className={styles.content}>
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
